import React, { useEffect, useReducer, useState } from 'react';
import { SortingState } from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { RiFilter3Fill } from 'react-icons/ri';
import 'react-toastify/dist/ReactToastify.css';

import { fetchTablesAllRest } from '@/services/table/table-service';
import DebouncedInput from '@/components/DebounceInput';
import Button, { ButtonSizes, ButtonVariants } from '@/components/Button/Button';
import { showToast } from '@/common/showToast';
import Paginator from '@/pages/common/paginator';
import StandardLayout from '@/common/layouts/StandardLayout';
import { convertSortingToApiFormat } from '@/common/utils/convert-sorting-to-api-format';
import TablesTable from './components/TablesTable';
import TablesFilter from './components/TablesFilter';
import { initialState, TableFilterActionType, tableQueryReducer } from './table-query-reducer';

const Tables: React.FC = () => {
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [isDebouncing, setIsDebouncing] = useState<boolean>(false);

  const [state, dispatch] = useReducer(tableQueryReducer, initialState);

  const { filter, skip, take, sorting } = state;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['tableData', state],
    queryFn: () =>
      fetchTablesAllRest({
        filter,
        skip,
        take,
        sorts: convertSortingToApiFormat(sorting),
      }),
    staleTime: 60000,
  });

  useEffect(() => {
    if (isError) {
      showToast(`Error when fetching server data: ${error.message}`, 'warning');
    }
  }, [isError, error?.message]);

  const handleSearch = (value: string | number) => {
    const searchValue = value.toString();
    if (searchValue === state.filter.name) return;
    setIsDebouncing(true);
    dispatch({ type: TableFilterActionType.SET_NAME, payload: searchValue });
    setIsDebouncing(false);
  };

  const handlePaginationChange = (skip: number, take: number) => {
    dispatch({ type: TableFilterActionType.SET_PAGINATION, payload: { skip, take } });
  };

  const handleSortingChange = (sorting: SortingState) => {
    dispatch({ type: TableFilterActionType.SET_SORTING, payload: sorting });
  };

  const hasActiveFilters = Object.values(state.filter).some(Boolean);

  return (
    <StandardLayout pageTitle="Tables">
      <div className="d-flex flex-column gap-3">
        <div className="w-100 d-flex justify-content-between align-items-center gap-2 py-2">
          <div className="w-100 d-flex align-items-center justify-content-between gap-2">
            <div className="d-flex align-items-center gap-2">
              <DebouncedInput
                className="form-control w-auto"
                placeholder="Search tables..."
                onChange={handleSearch}
                value={state.filter.name || ''}
                isLoading={isDebouncing}
              />
              <Button
                onClick={() => setShowFilterModal(true)}
                className="btn btn-outline d-flex align-items-center justify-content-center gap-2"
                variant={ButtonVariants.secondary}
                size={ButtonSizes.sm}
              >
                <RiFilter3Fill size={20} />
                Filter
              </Button>
              {hasActiveFilters && (
                <Button
                  size={ButtonSizes.sm}
                  variant={ButtonVariants.primary}
                  onClick={() => dispatch({ type: TableFilterActionType.RESET_FILTERS })}
                  className="btn btn-primary d-flex align-items-center justify-content-center gap-2"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        </div>

        <TablesTable
          data={data?.tables || []}
          isLoading={isLoading}
          sorting={state.sorting}
          onSortingChange={handleSortingChange}
        />

        <Paginator
          take={state.take}
          skip={state.skip}
          showing={data?.tables.length || 0}
          totalCount={data?.totalCount || 0}
          setTake={(take) => handlePaginationChange(state.skip, take)}
          setSkip={(skip) => handlePaginationChange(skip, state.take)}
        />
      </div>

      {showFilterModal && (
        <TablesFilter
          onClose={() => setShowFilterModal(false)}
          onApplyFilters={(filters) => {
            dispatch({ type: TableFilterActionType.SET_FILTERS, payload: filters });
            setShowFilterModal(false);
          }}
          currentFilters={state.filter}
        />
      )}
    </StandardLayout>
  );
};

export default Tables;
